import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
import { BASE_URL } from "../../../../BaseUrl";
import WaiterChartWidgetV2 from "./charts/WaiterChartWidgetV2.js";
import WaiterTableListWidgetV2 from "./charts/WaiterTableListWidgetV2.js";

import {
  FiBarChart2,
  FiPieChart,
  FiAward,
  FiList,
  FiUsers,
  FiTrendingUp,
  FiShoppingBag,
  FiInfo,
} from "react-icons/fi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { _t, getCookie } from "../../../../functions/Functions";
import "./WaiterPerformanceWidget.css";



const WaiterSkeletonLoader = () => (
  <div className="waiter-skeleton-container mb-4">
    {/* Stat Cards Skeleton */}
    <div className="waiter-stats-grid mb-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="waiter-stat-col">
          <div className="waiter-stat-card p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="waiter-skeleton-box" style={{ width: "32px", height: "32px", borderRadius: "8px" }} />
              <div className="waiter-skeleton-box" style={{ width: "50px", height: "18px", borderRadius: "10px" }} />
            </div>
            <div className="waiter-skeleton-box mb-2" style={{ width: "60%", height: "26px" }} />
            <div className="waiter-skeleton-box" style={{ width: "80%", height: "14px" }} />
          </div>
        </div>
      ))}
    </div>

    {/* Charts Skeleton Grid */}
    <div className="row gx-3 gy-4 mb-4">
      {[1, 2].map((i) => (
        <div key={i} className="col-12 col-lg-6">
          <div className="waiter-v2-chart-card p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <div className="waiter-skeleton-box" style={{ width: "32px", height: "32px", borderRadius: "8px" }} />
                <div className="d-flex flex-column gap-1">
                  <div className="waiter-skeleton-box mb-1" style={{ width: "140px", height: "16px" }} />
                  <div className="waiter-skeleton-box" style={{ width: "90px", height: "12px" }} />
                </div>
              </div>
              <div className="waiter-skeleton-box" style={{ width: "80px", height: "26px", borderRadius: "6px" }} />
            </div>
            <div className="waiter-skeleton-box w-100" style={{ height: "200px" }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RenderWidget = ({ card, date }) => {
  if (!card) return null;
  const { type, data, heading, size = "col-12" } = card;

  switch (type) {
    case "bars_widget": {
      const isCashier = heading && (heading.toLowerCase().includes("user") || heading.toLowerCase().includes("cashier"));
      const defaultViewMode = isCashier ? "bar" : "hbar";
      return (
        <div className={`${size} mb-3`}>
          <WaiterChartWidgetV2 data={data} heading={heading} defaultView={defaultViewMode} />
        </div>
      );
    }
    case "donut_widget":
      return (
        <div className={`${size} mb-3`}>
          <WaiterChartWidgetV2 data={data} heading={heading} defaultView="donut" />
        </div>
      );
    case "online_widget":
      return (
        <div className={`${size} mb-3`}>
          <WaiterTableListWidgetV2 data={data} heading={heading} size="col-12" date={date} />
        </div>
      );
    default:
      return null;
  }
};

const WaiterPerformanceWidget = ({
  waiterData: propWaiterData = null,
  date,
  loading: propLoading = false,
  startDate = null,
  endDate = null,
  branch = null,
  activeKey = "waiter",
  filterTrigger = 0,
}) => {
  const { t } = useTranslation();
  const [internalWaiterData, setInternalWaiterData] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);

  const buildFilterPayload = useCallback(() => {
    const payload = {};
    const branchData = branch?.length
      ? branch.map((b) => b.id)
      : branch?.id
        ? [branch.id]
        : "";
    if (branchData && branchData.length) payload.branch_id = branchData;
    payload.from = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    payload.to = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    return payload;
  }, [startDate, endDate, branch]);

  const fetchWaiterApis = useCallback(async () => {
    if (propWaiterData && propWaiterData.length > 0) {
      setInternalWaiterData(propWaiterData);
      return;
    }

    setInternalLoading(true);
    const formData = buildFilterPayload();

    const cardsUrl = `${BASE_URL}/widgets/waiter-dash-cards`;
    const cardsPromise = axios
      .post(
        cardsUrl,
        { from: formData.from, to: formData.to, ...(formData.branch_id ? { branch_id: formData.branch_id } : {}) },
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      )
      .then((res) => res.data)
      .catch((err) => ({ status: false, message: err?.message || "Failed to fetch" }));

    const apis = [
      { prefix: "widgets", endpoint: "waiter-bar-graph", method: "POST", params: ["branch_id", "from", "to"], heading: "Waiter Activity", web_type: "bars_widget", size: "col-6" },
      { prefix: "widgets", endpoint: "user-bar-graph", method: "POST", params: ["branch_id", "from", "to"], heading: "Cashier Activity", web_type: "bars_widget", size: "col-6" },
      { prefix: "widgets", endpoint: "waiter-sales-piechart", method: "POST", params: ["branch_id", "from", "to"], heading: "Waiter Revenue Share", web_type: "donut_widget", size: "col-6" },
      { prefix: "widgets", endpoint: "user-sales-piechart", method: "POST", params: ["branch_id", "from", "to"], heading: "Cashier Revenue Share", web_type: "donut_widget", size: "col-6" },
      { prefix: "widgets", endpoint: "waiter-tabular-data", method: "POST", params: ["branch_id", "from", "to"], heading: "Waiter Performance Breakdown", web_type: "online_widget", size: "col-12" },
      { prefix: "widgets", endpoint: "waiter-cancellation", method: "POST", params: ["branch_id", "from", "to"], heading: "Waiter Cancellation Breakdown", web_type: "online_widget", size: "col-12" },
      { prefix: "widgets", endpoint: "waiter-report", method: "POST", params: ["branch_id", "from", "to"], heading: "Top Waiters Leaderboard", web_type: "online_widget" },
      { prefix: "widgets", endpoint: "user-tabular-data", method: "POST", params: ["branch_id", "from", "to"], heading: "Cashier Performance Breakdown", web_type: "online_widget", size: "col-12" }
    ];

    try {
      const [cardsRes, ...responses] = await Promise.all([
        cardsPromise,
        ...apis.map((api) => {
          const url = `${BASE_URL}/${api.prefix}/${api.endpoint}`;
          const postBody = {};
          if (api.params.includes("branch_id") && formData.branch_id) {
            postBody.branch_id = formData.branch_id;
          }
          if (api.params.includes("from") && formData.from) {
            postBody.from = formData.from;
          }
          if (api.params.includes("to") && formData.to) {
            postBody.to = formData.to;
          }

          return axios
            .post(url, postBody, {
              headers: { Authorization: `Bearer ${getCookie()}` }
            })
            .then((res) => res.data)
            .catch((err) => ({ status: false, message: err?.message || "Failed to fetch" }));
        }),
      ]);

      if (cardsRes?.status == 1 || cardsRes?.status === true || cardsRes?.status === 200) {
        setStatsData(cardsRes.details || null);
      } else {
        setStatsData(null);
      }

      let newWaiterData = [];
      responses.forEach((res, index) => {
        if (apis[index].endpoint === "waiter-report") {
          if (res.status) {
            if (res.details) {
              newWaiterData.push({
                type: apis[index].web_type,
                heading: "Top 10 Waiter by sales",
                date: date,
                data: res.details,
                size: "col-6"
              });
            }
            if (res.order_count) {
              newWaiterData.push({
                type: apis[index].web_type,
                heading: "Top 10 Waiter by order",
                date: date,
                data: res.order_count,
                size: "col-6"
              });
            }
          } else {
            newWaiterData.push({
              type: apis[index].web_type,
              heading: apis[index].heading,
              date: date,
              data: { error: res.message }
            });
          }
        } else {
          newWaiterData.push({
            type: apis[index].web_type,
            heading: apis[index].heading,
            date: date,
            data: (res.status === true || res.status === 1 || res.details) ? (res.details || res) : { error: res.message },
            size: apis[index].size || "col-12"
          });
        }
      });

      setInternalWaiterData(newWaiterData);
    } catch (err) {
      console.error("Error fetching waiter performance data:", err);
      setStatsData(null);
    } finally {
      setInternalLoading(false);
    }
  }, [buildFilterPayload, date, propWaiterData]);

  useEffect(() => {
    if (activeKey === "waiter" && (!propWaiterData || propWaiterData.length === 0)) {
      fetchWaiterApis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, filterTrigger]);

  const waiterData = propWaiterData && propWaiterData.length > 0 ? propWaiterData : internalWaiterData;
  const loading = propLoading || internalLoading;

  // Categorize waiterData widgets
  const barWidgets = waiterData?.filter((card) => card.type === "bars_widget") || [];
  const pieWidgets = waiterData?.filter((card) => card.type === "donut_widget") || [];
  const leaderboardWidgets =
    waiterData?.filter(
      (card) => card.type === "online_widget" && card.heading?.toLowerCase().includes("top 10")
    ) || [];
  const tabularWidgets =
    waiterData?.filter(
      (card) => card.type === "online_widget" && !card.heading?.toLowerCase().includes("top 10")
    ) || [];

  const uncategorizedWidgets =
    waiterData?.filter(
      (card) =>
        card.type !== "bars_widget" &&
        card.type !== "donut_widget" &&
        card.type !== "online_widget"
    ) || [];

  const formatStatAmount = (value) => {
    const num = Number(value);
    if (value == null || value === "" || Number.isNaN(num)) return "--";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const totalActiveStaff = statsData?.distinct_staff ?? 0;
  const totalPeriodOrders = statsData?.total_orders ?? 0;
  const topSalesWaiter = statsData?.top_waiter_by_sales?.name || "--";
  const topSalesAmount = formatStatAmount(statsData?.top_waiter_by_sales?.total_sales);
  const topOrderWaiter = statsData?.top_waiter_by_orders?.name || "--";
  const topOrderCount =
    statsData?.top_waiter_by_orders?.total_orders != null
      ? String(statsData.top_waiter_by_orders.total_orders)
      : "--";
  const topUserBySales = statsData?.top_user_by_sales?.name || "--";
  const topUserSalesAmount = formatStatAmount(statsData?.top_user_by_sales?.total_sales);
  const hasStats = Boolean(statsData);

  if (loading) {
    return (
      <div className="waiter-performance-container">
        <WaiterSkeletonLoader />
      </div>
    );
  }

  return (
    <div className="waiter-performance-container">
      {/* Finance-style Top Stat Cards */}
      {hasStats && (
        <div className="waiter-stats-grid">
          {/* Stat 1: Total Active Staff */}
          <div className="waiter-stat-col">
            <div className="waiter-stat-card waiter-card-primary">
              <div className="waiter-card-header">
                <div className="waiter-card-icon-title">
                  <div className="waiter-card-icon-box waiter-card-icon-primary">
                    <FiUsers size={16} />
                  </div>
                  <span className="waiter-card-title">
                    {_t(t("Active Staff"))}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tt-active-staff">{_t(t("Total number of active waiters and cashiers recorded in this period."))}</Tooltip>}
                    >
                      <span><FiInfo size={12} color="#9ca3af" className="ml-1" style={{ cursor: "pointer" }} /></span>
                    </OverlayTrigger>
                  </span>
                </div>
                <span className="waiter-card-badge waiter-badge-primary">{_t(t("Active"))}</span>
              </div>
              <div className="waiter-card-value">{totalActiveStaff}</div>
              <div className="waiter-card-subtext">{_t(t("Staff members with logged entries"))}</div>
            </div>
          </div>

          {/* Stat 2: Top Sales Performer */}
          <div className="waiter-stat-col">
            <div className="waiter-stat-card waiter-card-amber">
              <div className="waiter-card-header">
                <div className="waiter-card-icon-title">
                  <div className="waiter-card-icon-box waiter-card-icon-amber">
                    <FiAward size={16} />
                  </div>
                  <span className="waiter-card-title">
                    {_t(t("Top Sales Performer"))}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tt-top-sales">{_t(t("Waiter with the highest overall sales revenue."))}</Tooltip>}
                    >
                      <span><FiInfo size={12} color="#9ca3af" className="ml-1" style={{ cursor: "pointer" }} /></span>
                    </OverlayTrigger>
                  </span>
                </div>
                <span className="waiter-card-badge waiter-badge-amber">
                  {_t(t("Top Revenue"))}
                </span>
              </div>
              <div className="waiter-card-value" style={{ fontSize: "17px" }} title={topSalesWaiter}>{topSalesWaiter}</div>
              <div className="waiter-card-subtext">{_t(t("Sales Value"))}: <strong>{topSalesAmount}</strong></div>
            </div>
          </div>

          {/* Stat 3: Top Order Volume */}
          <div className="waiter-stat-col">
            <div className="waiter-stat-card waiter-card-emerald">
              <div className="waiter-card-header">
                <div className="waiter-card-icon-title">
                  <div className="waiter-card-icon-box waiter-card-icon-emerald">
                    <FiShoppingBag size={16} />
                  </div>
                  <span className="waiter-card-title">
                    {_t(t("Highest Order Volume"))}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tt-top-orders">{_t(t("Waiter who served the highest total number of orders."))}</Tooltip>}
                    >
                      <span><FiInfo size={12} color="#9ca3af" className="ml-1" style={{ cursor: "pointer" }} /></span>
                    </OverlayTrigger>
                  </span>
                </div>
                <span className="waiter-card-badge waiter-badge-emerald">
                  {_t(t("Top Volume"))}
                </span>
              </div>
              <div className="waiter-card-value" style={{ fontSize: "17px" }} title={topOrderWaiter}>{topOrderWaiter}</div>
              <div className="waiter-card-subtext">{_t(t("Total Orders"))}: <strong>{topOrderCount}</strong></div>
            </div>
          </div>

          {/* Stat 4: Staff Orders Volume */}
          <div className="waiter-stat-col">
            <div className="waiter-stat-card waiter-card-indigo">
              <div className="waiter-card-header">
                <div className="waiter-card-icon-title">
                  <div className="waiter-card-icon-box waiter-card-icon-indigo">
                    <FiTrendingUp size={16} />
                  </div>
                  <span className="waiter-card-title">
                    {_t(t("Staff Orders Volume"))}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tt-staff-volume">{_t(t("Total orders served across all active waiters & cashiers."))}</Tooltip>}
                    >
                      <span><FiInfo size={12} color="#9ca3af" className="ml-1" style={{ cursor: "pointer" }} /></span>
                    </OverlayTrigger>
                  </span>
                </div>
                <span className="waiter-card-badge waiter-badge-indigo">
                  {_t(t("Total Volume"))}
                </span>
              </div>
              <div className="waiter-card-value">{totalPeriodOrders}</div>
              <div className="waiter-card-subtext">
                {_t(t("Top User"))}: <strong title={topUserBySales}>{topUserBySales}</strong>
                {topUserSalesAmount !== "--" ? ` (${topUserSalesAmount})` : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !hasStats && (!waiterData || waiterData.length === 0) && (
        <div className="waiter-empty-state">
          <FiUsers className="waiter-empty-icon" />
          <h5 className="font-weight-bold text-dark">{_t(t("No Performance Data Available"))}</h5>
          <p className="text-muted small mb-0">
            {_t(t("Select a valid date range or branch filter to generate waiter and cashier performance analytics."))}
          </p>
        </div>
      )}

      {/* 1. Bar Graphs Section */}
      {barWidgets.length > 0 && (
        <>
          <div className="waiter-section-header">
            <h5 className="waiter-section-title">
              <FiBarChart2 color="#2563eb" size={15} />
              {_t(t("Order Activity Trends"))}
            </h5>
          </div>
          <div className="row gx-3 gy-4 mb-3">
            {barWidgets.map((card, index) => (
              <RenderWidget key={`bar-${index}`} card={card} date={date} />
            ))}
          </div>
        </>
      )}

      {/* 2. Pie / Donut Charts Section */}
      {pieWidgets.length > 0 && (
        <>
          <div className="waiter-section-header">
            <h5 className="waiter-section-title">
              <FiPieChart color="#2563eb" size={15} />
              {_t(t("Sales Distribution Share"))}
            </h5>
          </div>
          <div className="row gx-3 gy-4 mb-3">
            {pieWidgets.map((card, index) => (
              <RenderWidget key={`pie-${index}`} card={card} date={date} />
            ))}
          </div>
        </>
      )}

      {/* 3. Leaderboards (Top Waiters) Section */}
      {leaderboardWidgets.length > 0 && (
        <>
          <div className="waiter-section-header">
            <h5 className="waiter-section-title">
              <FiAward color="#d97706" size={15} />
              {_t(t("Top Performers Leaderboard"))}
            </h5>
            <span className="waiter-badge-count">{leaderboardWidgets.length} {_t(t("Lists"))}</span>
          </div>
          <div className="row gx-3 gy-4 mb-3">
            {leaderboardWidgets.map((card, index) => (
              <RenderWidget key={`leaderboard-${index}`} card={card} date={date} />
            ))}
          </div>
        </>
      )}



      {/* 4. Tabular Data Section */}
      {tabularWidgets.length > 0 && (
        <>
          <div className="waiter-section-header">
            <h5 className="waiter-section-title">
              <FiList color="#059669" size={15} />
              {_t(t("Detailed Performance Records"))}
            </h5>
            <span className="waiter-badge-count">{tabularWidgets.length} {_t(t("Tables"))}</span>
          </div>
          <div className="row gx-3 gy-4 mb-3">
            {tabularWidgets.map((card, index) => (
              <RenderWidget key={`tabular-${index}`} card={card} date={date} />
            ))}
          </div>
        </>
      )}

      {/* Uncategorized fallback */}
      {uncategorizedWidgets.length > 0 && (
        <div className="row gx-3 gy-4 mb-3">
          {uncategorizedWidgets.map((card, index) => (
            <RenderWidget key={`uncat-${index}`} card={card} date={date} />
          ))}
        </div>
      )}
    </div>
  );
};

export default WaiterPerformanceWidget;
